import React, { useState } from 'react';
import { X, MapPin, Package2, Clock, CheckCircle, Loader } from 'lucide-react';
import { ItemLocationInfo } from '../../../../../services/warehouseTransferService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface LocationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (location: ItemLocationInfo) => void;
  itemNo: string;
  itemDescription: string;
  locations: ItemLocationInfo[];
  loading: boolean;
  viewMode?: boolean;  // Ny prop: true for read-only visning
}

export const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  itemNo,
  itemDescription,
  locations,
  loading,
  viewMode = false
}) => {
  const { t } = useTranslation();
  const [selectedLocation, setSelectedLocation] = useState<ItemLocationInfo | null>(null);

  if (!isOpen) return null;

  const handleSelect = (location: ItemLocationInfo) => {
    if (viewMode) return; // Ikke gjør noe i viewMode
    onSelect(location);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
        <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg shadow-xl sm:max-w-2xl sm:max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <Package2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold">
                  {viewMode ? t('warehouse.modules.warehouseTransfer.modals.selectLocation.titleItemBalance') : t('warehouse.modules.warehouseTransfer.modals.selectLocation.title')}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {itemNo} - {itemDescription}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin h-8 w-8 text-blue-500" />
                <span className="ml-2 text-sm sm:text-base">{t('warehouse.modules.warehouseTransfer.modals.selectLocation.loading')}</span>
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-8">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-sm sm:text-base">{t('warehouse.modules.warehouseTransfer.modals.selectLocation.noLocations')}</p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  {t('warehouse.modules.warehouseTransfer.modals.selectLocation.noLocationsDescription')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-xs sm:text-sm text-gray-600 mb-4">
                  {t('warehouse.modules.warehouseTransfer.modals.selectLocation.itemAtLocations', { '0': locations.length, '1': locations.length !== 1 ? 'er' : '' })}
                </div>

                {locations.map((location, index) => (
                  <div
                    key={`${location.locationName}-${index}`}
                    onClick={() => handleSelect(location)}
                    className={`border border-gray-200 rounded-lg p-4 transition-all min-h-[44px] ${
                      viewMode
                        ? 'cursor-default'
                        : 'hover:border-blue-300 hover:bg-blue-50 cursor-pointer active:bg-blue-100'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <MapPin className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold font-mono text-sm break-all">
                            {location.locationName}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600">
                            {t('warehouse.modules.warehouseTransfer.modals.selectLocation.warehouseLabel', { '0': location.warehouseID })}
                          </div>
                        </div>
                      </div>

                      <div className="text-left sm:text-right flex-shrink-0">
                        <div className="font-semibold text-green-600 text-sm sm:text-base">
                          {location.availableQuantity} {t('warehouse.modules.warehouseTransfer.labels.available')}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500">
                          {location.totalQuantity} {t('warehouse.modules.warehouseTransfer.labels.total')}
                          {location.reservedQuantity > 0 && (
                            <span className="ml-1 text-orange-600">
                              ({location.reservedQuantity} {t('warehouse.modules.warehouseTransfer.labels.reserved')})
                            </span>
                          )}
                        </div>
                        {location.availableSpace != null && location.maxQuantity != null && (
                          <div className="text-xs sm:text-sm text-blue-600 mt-1">
                            {location.availableSpace.toFixed(0)} {t('warehouse.modules.warehouseTransfer.labels.availableSpace')}
                            <span className="text-gray-400"> ({t('warehouse.modules.warehouseTransfer.labels.of')} {location.maxQuantity.toFixed(0)})</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional info */}
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-500">
                      {location.binCodePrimary && (
                        <div className="flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                          <span>{t('warehouse.modules.warehouseTransfer.labels.primaryLocation')}</span>
                        </div>
                      )}

                      {location.binCodeFixed && (
                        <div className="flex items-center">
                          <span className="w-3 h-3 bg-blue-500 rounded-full mr-1 flex-shrink-0"></span>
                          <span>{t('warehouse.modules.warehouseTransfer.labels.fixedLocation')}</span>
                        </div>
                      )}

                      {location.lastMovementDate && (
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span>
                            {t('warehouse.modules.warehouseTransfer.labels.lastMoved')}: {location.lastMovementDate.toLocaleDateString('no-NO')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 sm:p-6 border-t">
            <button
              onClick={onClose}
              className="px-6 py-3 sm:px-4 sm:py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] sm:min-h-0 text-sm sm:text-base"
            >
              {t('common.buttons.cancel')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};